import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Table, Spinner, Badge, Row, Container, Button } from 'react-bootstrap';
import axios from 'axios';
import { toWords } from 'number-to-words';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';

const ViewSalesPD = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);
  const [rows, setRows] = useState([]);

  const fetchSalesData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}sales/findById/${id}`, {
        headers: authHeader()
      });

      const data = response.data;
      setSalesData(data);

      if (data.salesItems && data.salesItems.length > 0) {
        setRows(data.salesItems.map(item => ({
          description: item.product?.description || '',
          productremarks: item.productremarks || "",
          quantity: item.quantity,
          unitrate: item.unitrate,
          amount: item.amount,
          hsnsacno: item.hsnsacno,
          partno: item.partno,
          serialnumber: item.serialnumber,
          modelnumber: item.modelnumber,
          tagnumber: item.tagnumber,
          moc: item.moc || item.product?.moc || "",
          itemcode: item.itemcode || "",
          uom: item.uom,
          gst: item.gst,
          totalinr: item.totalinr
        })));
      }
    } catch (error) {
      console.error("Error fetching sales data", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sales data. Please try again.",
      });
      navigate("/salesOrderIndexTable/1");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value || 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Draft': return 'secondary';
      case 'Pending': return 'warning';
      case 'Completed': return 'success';
      case 'Cancelled': return 'danger';
      case 'Paid': return 'success';
      case 'Unpaid': return 'warning';
      default: return 'info';
    }
  };

  const getAddressString = (address) => {
    if (!address) return '-';
    return [
      address.customeraddress,
      address.city?.cityname,
      address.state?.statename,
      address.pincode,
      address.country?.countryname
    ].filter(Boolean).join(', ');
  };

  const numberToWordsWithRupees = (num) => {
    if (isNaN(num)) return "Zero Rupees Only";
    const amount = Math.round(num);
    const words = toWords(amount);
    const capitalizedWords = words.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    return `${capitalizedWords} Rupees Only`;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!salesData) {
    return <div className="alert alert-danger text-center mt-4">Sales Invoice not found</div>;
  }

  // Calculate totals
  const nettotal = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const pAndF = nettotal * (salesData.packingandforwardingpercent / 100);
  const freight = nettotal * (salesData.freightpercent / 100);
  const subtotal = nettotal + pAndF + freight;
  const gst = subtotal * (salesData.gstpercent / 100);
  const grandtotalinr = subtotal + gst;
  const roundedGrandTotal = Math.round(grandtotalinr);

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-primary mb-0">SALES INVOICE - VIEW</h5>
            <div>
              <Button variant="outline-primary" size="sm" className="me-2"
                onClick={() => navigate(`/salesInvoice/print/${id}`)}
              >
                <FaPrint className="me-1" />
                Print
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-1" />
                Back
              </Button>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Invoice Number</small>
                <div className="fw-bold">{salesData.invoiceno || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Invoice Date</small>
                <div className="fw-bold">{formatDate(salesData.invoicedate)}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">DC Number</small>
                <div className="fw-bold">{salesData.dcno || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg={getStatusBadge(salesData.status)} className="px-2 py-1">
                    {salesData.status || '-'}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Sales Invoice Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Sales Invoice Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">DC Date</small>
              <div className="fw-bold">{formatDate(salesData.dcdate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Buyer Order No</small>
              <div className="fw-bold">{salesData.buyerorderno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Buyer Order Date</small>
              <div className="fw-bold">{formatDate(salesData.buyerorderdate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Tally Reference No</small>
              <div className="fw-bold">{salesData.tallyreferenceno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Dispatch Through</small>
              <div className="fw-bold">{salesData.dispatchthrough || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Destination</small>
              <div className="fw-bold">{salesData.destination || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Delivery Term</small>
              <div className="fw-bold">{salesData.termsofdelivery || '-'}</div>
            </Col>
            {salesData.reference && (
              <Col md={12}>
                <small className="text-muted">Other Reference</small>
                <div className="fw-bold">{salesData.reference}</div>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* Customer Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Customer Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Company Name</small>
              <div className="fw-bold">{salesData.customer?.companyname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">GST No</small>
              <div className="fw-bold">{salesData.customer?.gstno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Contact Person</small>
              <div className="fw-bold">{salesData.contactperson || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Phone</small>
              <div className="fw-bold">{salesData.contactno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Email</small>
              <div className="fw-bold">{salesData.email || '-'}</div>
            </Col>
            <Col md={12}>
              <small className="text-muted">Address</small>
              <div className="fw-bold">{getAddressString(salesData.customeraddress)}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Bill To & Ship To */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Billing & Shipping Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={6}>
              <div className="border-start border-3 border-primary ps-3">
                <small className="text-muted">Bill To</small>
                <div className="fw-bold">{getAddressString(salesData.billto)}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="border-start border-3 border-success ps-3">
                <small className="text-muted">Ship To</small>
                <div className="fw-bold">{getAddressString(salesData.shipto)}</div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Branch & Warehouse Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Branch & Warehouse Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={6}>
              <small className="text-muted">Branch</small>
              <div className="fw-bold">{salesData.branch?.branchname || '-'}</div>
            </Col>
            <Col md={6}>
              <small className="text-muted">Warehouse</small>
              <div className="fw-bold">{salesData.warehouse?.warehousename || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Sales Items */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Sales Items</h6>
        </div>
        <div className="p-0">
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-center fw-bold py-2">#</th>
                  <th className="fw-bold py-2">Description</th>
                  <th className="fw-bold py-2">Part No</th>
                  <th className="fw-bold py-2">HSN Code</th>
                  <th className="fw-bold py-2">Serial No</th>
                  <th className="fw-bold py-2">Model No</th>
                  <th className="fw-bold py-2">Tag No</th>
                  <th className="fw-bold py-2">MOC</th>
                  <th className="fw-bold py-2">Item Code</th>
                  <th className="fw-bold py-2 text-center">Qty</th>
                  <th className="fw-bold py-2 text-center">UOM</th>
                  <th className="fw-bold py-2 text-end">Unit Price</th>
                  <th className="fw-bold py-2 text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      <div className="fw-semibold">{row.description || '-'}</div>
                      {row.productremarks && row.productremarks !== '-' && (
                        <div className="text-muted small mt-1">
                          <small><strong>Remarks:</strong> {row.productremarks}</small>
                        </div>
                      )}
                    </td>
                    <td>{row.partno || '-'}</td>
                    <td>{row.hsnsacno || '-'}</td>
                    <td>{row.serialnumber || '-'}</td>
                    <td>{row.modelnumber || '-'}</td>
                    <td>{row.tagnumber || '-'}</td>
                    <td>{row.moc || '-'}</td>
                    <td>{row.itemcode || '-'}</td>
                    <td className="text-center">{row.quantity || '0'}</td>
                    <td className="text-center">{row.uom || '-'}</td>
                    <td className="text-end">{formatCurrency(row.unitrate)}</td>
                    <td className="text-end">{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan="12" className="text-end fw-bold py-2">Net Total:</td>
                  <td className="text-end fw-bold py-2">{formatCurrency(nettotal)}</td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      </div>

      {/* Calculation Summary */}
      <Row className="mb-3">
        <Col lg={6} className="ms-auto">
          <div className="shadow-sm border rounded bg-white">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
            </div>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Net Total:</span>
                <span className="fw-bold">{formatCurrency(nettotal)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">
                  Packing & Forwarding ({salesData.packingandforwardingpercent || '0'}%):
                </span>
                <span className="fw-bold">{formatCurrency(pAndF)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">
                  Freight ({salesData.freightpercent || '0'}%):
                </span>
                <span className="fw-bold">{formatCurrency(freight)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Subtotal:</span>
                <span className="fw-bold">{formatCurrency(subtotal)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">
                  GST ({salesData.gstpercent || '18'}%):
                </span>
                <span className="fw-bold">{formatCurrency(gst)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-top">
                <span className="fw-bold">Grand Total</span>
                <span className="fw-bold text-primary">{formatCurrency(grandtotalinr)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold">Rounded Total</span>
                <span className="fw-bold text-success">{formatCurrency(roundedGrandTotal)}</span>
              </div>
              <div className="mt-3 pt-2 border-top">
                <div className="text-center">
                  <small className="text-muted fw-bold">Amount in Words</small>
                  <div className="fw-semibold text-dark small">
                    {numberToWordsWithRupees(roundedGrandTotal)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
          <FaArrowLeft className="me-1" />
          Back to List
        </Button>
      </div>
    </Container>
  );
};

export default ViewSalesPD;